import { presentChange } from '../../services/yodaChangelog';

/** Illustrated slides in the update carousel (plus the final “and more” slide). */
export const ILLUSTRATED_SLIDE_TARGET = 4;
export const UPCOMING_RELEASE_VERSION = '__upcoming__';

const VISIBLE_CHANGE_TYPES = new Set(['feature', 'fix', 'ui', 'i18n', 'build']);

const toVersionNumber = (version) => {
    const [major, minor, patch] = String(version || '').split('.').map((s) => parseInt(s, 10) || 0);
    return (major * 10000) + (minor * 100) + patch;
};

const isNewerVersion = (a, b) => toVersionNumber(a) > toVersionNumber(b);

const isVisiblePresented = (presented) => (
    VISIBLE_CHANGE_TYPES.has(presented.type) && Boolean(presented.title)
);

/**
 * Prefer illustrated changes newer than `appVersion`, then soft-fill from history.
 * Without `appVersion` (local/dev), upcoming + the last illustrated (or titled)
 * changes so `?swUpdate=1` previews the real carousel.
 */
export const collectChangelogHighlights = (releases, appVersion, upcoming = []) => {
    const hasAppVersion = Boolean(appVersion);
    const upcomingRelease = (!hasAppVersion && upcoming?.length)
        ? [{ version: UPCOMING_RELEASE_VERSION, changes: upcoming }]
        : [];
    const newestFirst = [...upcomingRelease, ...(releases || []).slice().reverse()];
    const highlights = [];
    const usedChanges = new Set();
    const releasesUsedForImages = new Set();
    let hasNewerRelease = false;

    const takeHighlight = (release, change, presented) => {
        highlights.push(presented);
        usedChanges.add(change);
        releasesUsedForImages.add(release.version);
    };

    for (const release of newestFirst) {
        if (hasAppVersion && isNewerVersion(release.version, appVersion))
            hasNewerRelease = true;
        for (const change of release.changes || []) {
            if (highlights.length >= ILLUSTRATED_SLIDE_TARGET)
                break;
            const presented = presentChange(change);
            if (!isVisiblePresented(presented) || !presented.imageUrl)
                continue;
            takeHighlight(release, change, presented);
        }
        if (highlights.length >= ILLUSTRATED_SLIDE_TARGET)
            break;
    }

    if (highlights.length === 0) {
        for (const release of newestFirst) {
            for (const change of release.changes || []) {
                if (highlights.length >= ILLUSTRATED_SLIDE_TARGET)
                    break;
                const presented = presentChange(change);
                if (!isVisiblePresented(presented) || usedChanges.has(change))
                    continue;
                takeHighlight(release, change, presented);
            }
            if (highlights.length >= ILLUSTRATED_SLIDE_TARGET)
                break;
        }
    }

    const includeReleaseForOthers = (release) => {
        if (!hasAppVersion)
            return release.version === UPCOMING_RELEASE_VERSION
                || releasesUsedForImages.has(release.version);
        if (hasNewerRelease)
            return isNewerVersion(release.version, appVersion);
        return release.version === appVersion || releasesUsedForImages.has(release.version);
    };

    const others = [];
    for (const release of newestFirst) {
        if (!includeReleaseForOthers(release))
            continue;
        for (const change of release.changes || []) {
            if (usedChanges.has(change))
                continue;
            const presented = presentChange(change);
            if (!isVisiblePresented(presented) || presented.imageUrl)
                continue;
            others.push(presented);
        }
    }

    return { highlights, others };
};
